import { format } from 'date-fns';
import { ProjectData } from '@/types/project';
import { formatCurrency, formatPercentage } from '@/utils/format';
import { logError } from '@/utils/logger';
import { calculateMetrics } from '@/utils/calculations';

/**
 * Prepares data for CSV export by formatting values and organizing rows
 */
export function prepareCSVData(data: ProjectData): string[][] {
  if (!data) {
    throw new Error('No data provided for CSV export');
  }

  // Compute metrics for summary section
  const metrics = calculateMetrics(data);

  return [
    ['Project CVR Report'],
    ['Generated on:', format(new Date(), 'dd/MM/yyyy HH:mm')],
    [],
    ['Project Details'],
    ['Project Name:', data.details.projectName || 'Not set'],
    ['Client Name:', data.details.clientName || 'Not set'],
    ['Start Date:', data.details.projectStartDate ? format(data.details.projectStartDate, 'dd/MM/yyyy') : 'Not set'],
    ['End Date:', data.details.projectEndDate ? format(data.details.projectEndDate, 'dd/MM/yyyy') : 'Not set'],
    ['Reporting Period:', formatReportingPeriod(data)],
    [],
    ['Summary'],
    ['Total Value:', formatCurrency(metrics.totalRevenue)],
    ['Total Costs:', formatCurrency(metrics.totalCosts)],
    ['Profit/Loss:', formatCurrency(metrics.profit)],
    ['Margin:', formatPercentage(metrics.margin)],
    [],
    ['Value'],
    ['Contract Value:', formatCurrency(data.value.contractValue || 0)],
    ['Approved Variations:', formatCurrency(data.value.approvedVariations || 0)],
    ['Unapproved Variations:', formatCurrency(data.value.unapprovedVariations || 0)],
    ['Invoiced to Date:', formatCurrency(data.value.invoicedToDate || 0)],
    ['Retention:', formatPercentage(data.value.retention || 0)],
    ['Forecasted Value:', formatCurrency(data.value.forecastedRevenue || 0)],
    [],
    ['Costs'],
    ...formatCostRows(data),
  ];
}

/**
 * Exports data to CSV file
 */
export function exportToCSV(data: ProjectData): void {
  try {
    const csvRows = prepareCSVData(data);
    const csvContent = csvRows
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.setAttribute('href', url);
    link.setAttribute('download', generateFilename(data));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

  } catch (error) {
    logError('CSV Export failed:', error);
    throw new Error('Failed to export CSV file. Please try again.');
  }
}

// Helper functions
function formatReportingPeriod(data: ProjectData): string {
  const start = data.details.reportingPeriodStart;
  const end = data.details.reportingPeriodEnd;
  
  if (!start || !end) return 'Not set';
  return `${format(start, 'dd/MM/yyyy')} to ${format(end, 'dd/MM/yyyy')}`;
}

function formatCostRows(data: ProjectData): string[][] {
  const rows: string[][] = [
    ['Component', 'To Date', 'Forecast', 'Total']
  ];

  const costKeys = ['labour', 'materials', 'plant', 'subcontractor', 'overheads', 'staff'];

  const costEntries = costKeys
    .filter(key => key in data.costs && data.costs[key as keyof typeof data.costs])
    .map(key => {
      const value = data.costs[key as keyof typeof data.costs];
      if (typeof value === 'object' && value !== null) {
        const toDate = value.toDate || 0;
        const forecast = value.forecast || 0;
        return [
          key.charAt(0).toUpperCase() + key.slice(1),
          formatCurrency(toDate),
          formatCurrency(forecast),
          formatCurrency(toDate + forecast)
        ];
      }
      return null;
    })
    .filter((row): row is string[] => row !== null);

  rows.push(...costEntries);

  // Calculate totals safely
  const totals = costKeys.reduce((acc, key) => {
    const value = data.costs[key as keyof typeof data.costs];
    if (typeof value === 'object' && value !== null) {
      return {
        toDate: acc.toDate + (value.toDate || 0),
        forecast: acc.forecast + (value.forecast || 0),
      };
    }
    return acc;
  }, { toDate: 0, forecast: 0 });

  rows.push([
    'TOTAL',
    formatCurrency(totals.toDate),
    formatCurrency(totals.forecast),
    formatCurrency(totals.toDate + totals.forecast)
  ]);

  return rows;
}

function generateFilename(data: ProjectData): string {
  const projectName = data.details.projectName
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_') || 'project';
  
  return `${projectName}_cvr_${format(new Date(), 'yyyy-MM-dd')}.csv`;
}